import { Formik } from 'formik';
import { useCallback } from 'react';

import { ActionItemKeys, NotificationResponse } from '@uplift/api';
import { trackConfirmedToDoCTA, useAllActionItems, useL10n } from '@uplift/hooks';
import { useUserDisplayNameQuery } from '@uplift/queries';
import { UserRole } from '@uplift/types';
import {
  CancelButton, FormModal, Loader, ModalActions, ModalContent, SubmitButton, TextInput
} from '@uplift/ui-v2';

export type CheckInModalProps = {
  clientName: string;
  open: boolean;
  handleClose: () => void;
  patientId: string;
  providerId: string;
};

type CheckInModalForm = {
  email: string;
};

export const CheckInModal: React.VFC<CheckInModalProps> = ({ clientName, open, handleClose, patientId, providerId }) => {
  const l10n = useL10n();
  const { data: name, isLoading: loading } = useUserDisplayNameQuery(patientId, {
    enabled: !!patientId,
  });
  const { dismiss } = useAllActionItems();

  const initial: CheckInModalForm = {
    email: l10n.dashboard.actionItems.clientScheduledPsych.modal.emailTemplate(name?.first ?? clientName)
  };

  const handleSubmit = useCallback(async (values: CheckInModalForm) => {
    await dismiss({
      key: ActionItemKeys.PROVIDER_PATIENT_SCHEDULED_PSYCH,
      response: NotificationResponse.email,
      id: patientId,
      providerId,
      providerNote: values.email
    });

    trackConfirmedToDoCTA({
      toDoType: ActionItemKeys.PROVIDER_PATIENT_SCHEDULED_PSYCH,
      userType: UserRole.provider
    });

    handleClose();
  }, [ dismiss, handleClose, patientId, providerId ]);

  if (loading) {
    return <Loader />;
  }

  return (
    <Formik initialValues={initial} onSubmit={handleSubmit}>
      {formik => (
        <FormModal
          open={open}
          onClose={handleClose}
          fullWidth
          maxWidth="md"
          title={l10n.dashboard.actionItems.clientScheduledPsych.modal.title(clientName)}
          subtitle={l10n.dashboard.actionItems.clientScheduledPsych.modal.subtitle}
        >
          <ModalContent>
            <TextInput
              name="email"
              label={l10n.dashboard.actionItems.clientScheduledPsych.modal.label}
              color="secondary"
              fullWidth
              multiline
            />
          </ModalContent>
          <ModalActions>
            <CancelButton onClick={handleClose}>Cancel</CancelButton>
            <SubmitButton
              color="secondary"
              disabled={!formik.isValid || formik.isSubmitting}
            >
              Send
            </SubmitButton>
          </ModalActions>
        </FormModal>
      )}
    </Formik>
  );
};
